import { useEffect } from "react";
import { useBuildingStore } from "@/hooks/useBuildingStore";
import { useAuth } from "@/hooks/useAuth";

interface Params {
  search?: string;
  archived?: boolean | null;
  orgId?: string;
  limit?: number;
}

export function useBuildingList(params: Params = {}) {
  const store = useBuildingStore();
  const { isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    // load on mount and when filters change, only when auth is ready and authenticated
    // Skip fetch if orgId not yet resolved to prevent showing buildings from wrong org
    if (isLoading || !isAuthenticated || !params.orgId) return;
    void store.listBuildings(params.orgId, {
      search: params.search,
      archived: params.archived ?? undefined,
      limit: params.limit,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.orgId, params.search, params.archived, params.limit, isAuthenticated, isLoading]);

  // Treat as loading if orgId hasn't resolved yet (effect hasn't fired)
  const isWaitingForOrg = !params.orgId && !store.list.isLoading;

  return {
    items: store.list.items,
    isLoading: store.list.isLoading || isWaitingForOrg,
    error: store.list.error,
    refetch: (override?: Params) =>
      isAuthenticated && !isLoading
        ? store.listBuildings(override?.orgId ?? params.orgId, {
            search: override?.search ?? params.search,
            archived: override?.archived ?? params.archived ?? undefined,
            limit: override?.limit ?? params.limit,
          })
        : Promise.resolve(),
  } as const;
}

// Note: Consumers should be wrapped with `observer` to react to store updates.
